import { type ColumnDef } from '@tanstack/react-table';

import { MoneyCell } from '@/components/composed/MoneyCell';
import { DateCell } from '@/components/composed/DateCell';
import { IdCell } from '@/components/composed/IdCell';
import { StatusBadge } from '@/components/composed/StatusBadge';
import type { TableFeatures } from '@/components/composed/DataTable/table-features';
import type { Invoice } from '@/lib/api/domains/invoices';
import { invoiceStatusMeta } from '../invoice-status';

/*
 * Column definitions bind domain data to composed cells (spec tier rule:
 * features/<x>/components know the domain). The DataTable owns rendering.
 */
export const invoiceColumns: Array<ColumnDef<TableFeatures, Invoice>> = [
  {
    accessorKey: 'number',
    header: 'Invoice #',
    // Invoice numbers read as machine codes in the proto (mono). Not muted —
    // this is the row's primary identifier, so keep it foreground-weight.
    cell: (info) => <IdCell value={info.getValue<string>()} muted={false} />,
  },
  {
    accessorKey: 'clientName',
    header: 'Client',
    cell: (info) => info.getValue<string>(),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: (info) => {
      const meta = invoiceStatusMeta(info.getValue<Invoice['status']>());
      return <StatusBadge label={meta.label} tone={meta.tone} />;
    },
  },
  {
    accessorKey: 'amountMinor',
    header: 'Amount',
    cell: (info) => (
      <MoneyCell
        amountMinor={info.getValue<number>()}
        currency={info.row.original.currency}
      />
    ),
  },
  {
    accessorKey: 'dueAt',
    header: 'Due',
    // A due date is a calendar day (no time-of-day), stored as midnight UTC —
    // format in UTC so it never shifts a day in a negative-offset timezone.
    cell: (info) => (
      <DateCell value={info.getValue<string>()} variant="calendar-day" />
    ),
  },
];
